import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Textarea,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  IconButton,
  VStack,
  useClipboard,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  FeeTreasuryBatchWithdrawPreviewResponse,
  FeeTreasuryBatchWithdrawExecutionResponse,
  FeeTreasuryOverview,
  FeeTreasuryTokenStats,
  FeeTreasuryWriteCapabilitiesResponse,
  FeeTreasuryWithdrawPreviewResponse,
  FeeTreasuryWhitelistResponse,
  FeeTreasuryWithdrawExecutionResponse,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
  TronOperationEnvelope,
  TronPendingOperationItem,
} from '@/types'
import { feeTreasuryService } from '@/services/admin/feeTreasuryService'
import { evmOperationService } from '@/services/admin/evmOperationService'
import { tronOperationService } from '@/services/admin/tronOperationService'
import { useAuth } from '@/contexts/AuthContext'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { getChainByChainId, getChainByAlias, onChainRegistryUpdate } from '@shared/chainRegistryClient'
import { getExplorerAddressUrl, truncateAddress } from '@/utils/formatUtils'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { FiCopy, FiExternalLink, FiRefreshCw } from 'react-icons/fi'
import { ethers, formatUnits } from 'ethers'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface FeeTreasuryDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  overview: FeeTreasuryOverview | null
}

interface BatchWithdrawItemForm {
  id: string
  token: string
  amount: string
}

const EVM_TERMINAL_STATES = new Set(['completed', 'rejected', 'failed'])
const TRON_TERMINAL_STATES = new Set(['completed', 'rejected', 'failed'])

const formatError = (error: unknown): string => {
  if (!error) {
    return 'Unknown error'
  }
  if (typeof error === 'string') {
    return error
  }
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'object') {
    const record = error as {
      response?: {
        data?: {
          error?: unknown
          message?: unknown
        } | string
      }
      message?: unknown
    }
    const responseData = record.response?.data
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData
    }
    if (responseData && typeof responseData === 'object') {
      if (typeof responseData.error === 'string' && responseData.error.trim()) {
        return responseData.error
      }
      if (typeof responseData.message === 'string' && responseData.message.trim()) {
        return responseData.message
      }
    }
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message
    }
  }
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

export function FeeTreasuryDetailsModal({ isOpen, onClose, overview }: FeeTreasuryDetailsModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const toast = useToast()
  const stickyBg = useColorModeValue('white', 'gray.800')
  const stickyBorderColor = useColorModeValue('gray.100', 'whiteAlpha.200')
  const tabListBg = useColorModeValue('gray.50', 'whiteAlpha.50')
  const tabListBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200')
  const tabColor = useColorModeValue('gray.600', 'gray.300')
  const tabHoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')
  const tabHoverColor = useColorModeValue('gray.900', 'white')
  const tabSelectedBg = useColorModeValue('white', 'gray.700')
  const tabSelectedBorderColor = useColorModeValue('blue.300', 'whiteAlpha.300')
  const tabSelectedColor = useColorModeValue('blue.700', 'blue.200')
  const tabSelectedShadow = useColorModeValue('sm', '0 10px 24px rgba(0, 0, 0, 0.28)')
  const tabDisabledColor = useColorModeValue('gray.400', 'gray.500')
  const amountPreviewBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const amountPreviewBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300')
  const amountPreviewColor = useColorModeValue('gray.700', 'gray.100')
  const batchItemsGridColumns = {
    base: '1fr',
    md: 'minmax(0, 1.35fr) minmax(0, 1fr) 132px 72px',
  }
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshingData, setIsRefreshingData] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tokens, setTokens] = useState<FeeTreasuryTokenStats[]>([])
  const [whitelist, setWhitelist] = useState<FeeTreasuryWhitelistResponse | null>(null)
  const [whitelistCount, setWhitelistCount] = useState<number>(0)
  const [whitelistWarning, setWhitelistWarning] = useState<string | null>(null)

  const [selectedToken, setSelectedToken] = useState<string>('')
  const [isRefreshingDiscrepancy, setIsRefreshingDiscrepancy] = useState(false)
  const [discrepancyList, setDiscrepancyList] = useState<{ token: string; difference: string }[] | null>(null)
  const [isLoadingDiscrepancyList, setIsLoadingDiscrepancyList] = useState(false)

  const [withdrawAmount, setWithdrawAmount] = useState<string>('')
  const [isCheckingWithdraw, setIsCheckingWithdraw] = useState(false)
  const [recipientAddress, setRecipientAddress] = useState<string>('')
  const [auditReason, setAuditReason] = useState<string>('')
  const [isExecutingWithdraw, setIsExecutingWithdraw] = useState(false)
  const [withdrawExecutionResult, setWithdrawExecutionResult] =
    useState<FeeTreasuryWithdrawExecutionResponse | null>(null)
  const [batchWithdrawItems, setBatchWithdrawItems] = useState<BatchWithdrawItemForm[]>([])
  const [isExecutingBatchWithdraw, setIsExecutingBatchWithdraw] = useState(false)
  const [batchWithdrawResult, setBatchWithdrawResult] =
    useState<FeeTreasuryBatchWithdrawExecutionResponse | null>(null)
  const [writeCapabilities, setWriteCapabilities] =
    useState<FeeTreasuryWriteCapabilitiesResponse | null>(null)
  const [withdrawPreviewResult, setWithdrawPreviewResult] =
    useState<FeeTreasuryWithdrawPreviewResponse | null>(null)
  const [batchWithdrawPreviewResult, setBatchWithdrawPreviewResult] =
    useState<FeeTreasuryBatchWithdrawPreviewResponse | null>(null)
  const [isPreviewingWithdraw, setIsPreviewingWithdraw] = useState(false)
  const [isPreviewingBatchWithdraw, setIsPreviewingBatchWithdraw] = useState(false)
  const [pendingConfirmAction, setPendingConfirmAction] =
    useState<'single_withdraw' | 'batch_withdraw' | null>(null)
  const [chainRegistryRevision, setChainRegistryRevision] = useState(0)
  const [pendingEvmOperation, setPendingEvmOperation] =
    useState<EvmPendingOperationItem | null>(null)
  const [evmOperation, setEvmOperation] = useState<EvmOperationEnvelope | null>(null)
  const [evmOperationError, setEvmOperationError] = useState<string | null>(null)
  const [hasQuarantinedEvmOperation, setHasQuarantinedEvmOperation] = useState(false)
  const [isRefreshingEvmOperation, setIsRefreshingEvmOperation] = useState(false)
  const [pendingTronOperation, setPendingTronOperation] =
    useState<TronPendingOperationItem | null>(null)
  const [tronOperation, setTronOperation] = useState<TronOperationEnvelope | null>(null)
  const [tronOperationError, setTronOperationError] = useState<string | null>(null)
  const [hasQuarantinedTronOperation, setHasQuarantinedTronOperation] = useState(false)
  const [isRefreshingTronOperation, setIsRefreshingTronOperation] = useState(false)
  const [canonicalChainRegistryId, setCanonicalChainRegistryId] = useState<string | null>(null)

  const [collectorAddress, setCollectorAddress] = useState<string>('')
  const [isCheckingCollector, setIsCheckingCollector] = useState(false)

  const [accountAddress, setAccountAddress] = useState<string>('')
  const [isCheckingWhitelist, setIsCheckingWhitelist] = useState(false)
  const cancelConfirmRef = useRef<HTMLButtonElement | null>(null)
  const batchItemIdRef = useRef(0)

  const chainIdentifier = useMemo(() => {
    if (!overview) {
      return ''
    }
    return overview.chainCode || overview.chainId.toString()
  }, [overview])

  const requestMode = useMemo<NetworkEnvironment | undefined>(() => {
    if (!overview) {
      return undefined
    }
    return overview.isTestnet ? 'testnet' : 'mainnet'
  }, [overview])

  const { hasCopied, onCopy } = useClipboard(overview?.contractAddress ?? '')

  const explorerUrl = useMemo(() => {
    if (!overview) return null
    return getExplorerAddressUrl(overview.chainId, overview.contractAddress) ?? null
  }, [overview])

  const chainEntry = useMemo(() => {
    if (!overview) return null
    return (
      getChainByChainId(Number(overview.chainId)) ??
      getChainByAlias(overview.chainKey) ??
      getChainByAlias(overview.chainCode) ??
      null
    )
  }, [chainRegistryRevision, overview])

  const isSolanaTreasury = useMemo(() => {
    return (overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase() === 'solana'
  }, [chainEntry?.compatibility, overview?.compatibility])

  const isEvmTreasury = useMemo(() => {
    return (overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase() === 'evm'
  }, [chainEntry?.compatibility, overview?.compatibility])

  const isTronTreasury = useMemo(() => {
    return (overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase() === 'tron'
  }, [chainEntry?.compatibility, overview?.compatibility])

  const hasNonterminalEvmOperation = isEvmTreasury
    && evmOperation !== null
    && !EVM_TERMINAL_STATES.has(evmOperation.state)

  const hasNonterminalTronOperation = isTronTreasury
    && tronOperation !== null
    && !TRON_TERMINAL_STATES.has(tronOperation.state)

  const isWithdrawTabEnabled = useMemo(() => {
    return !isSolanaTreasury
      && ['evm', 'tron'].includes((overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase())
  }, [chainEntry?.compatibility, isSolanaTreasury, overview?.compatibility])

  const compatibilityHint = useMemo(() => {
    const compatibility = (overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase()
    if (compatibility === 'tron') {
      return 'T...'
    }
    if (compatibility === 'solana') {
      return 'Base58 address'
    }
    return '0x...'
  }, [chainEntry?.compatibility, overview?.compatibility])

  const withdrawalSignerDisplay = useMemo(() => {
    const address = writeCapabilities?.withdrawalSignerAddress?.trim()
    if (!address) {
      return null
    }
    const compatibility = (overview?.compatibility ?? chainEntry?.compatibility ?? '').toLowerCase()
    if (compatibility === 'evm' && address.toLowerCase() === ethers.ZeroAddress.toLowerCase()) {
      return 'Not set'
    }
    return address
  }, [
    chainEntry?.compatibility,
    overview?.compatibility,
    writeCapabilities?.withdrawalSignerAddress,
  ])

  const whitelistOptions = useMemo(() => {
    return (whitelist?.addresses ?? []).map((address) => ({
      value: address,
      label: address,
    }))
  }, [whitelist?.addresses])

  const hasWhitelistRecipients = whitelistOptions.length > 0

  const tokenMetadataMap = useMemo(() => {
    const map = new Map<string, { symbol: string; decimals: number }>()
    if (!chainEntry) {
      return map
    }

    const normalizeTokenKey = (value: string) =>
      chainEntry.compatibility === 'evm' ? value.toLowerCase() : value

    chainEntry.tokens?.forEach(token => {
      if (token?.address) {
        map.set(normalizeTokenKey(token.address), {
          symbol: token.symbol || chainEntry.nativeCurrency?.symbol || 'TOKEN',
          decimals: token.decimals ?? chainEntry.nativeCurrency?.decimals ?? 18,
        })
      }
    })

    return map
  }, [chainEntry])

  const resolveTokenMeta = (
    tokenAddress: string
  ): { symbol: string; decimals: number | null; isKnownToken: boolean } => {
    const compatibility = (chainEntry?.compatibility ?? '').toLowerCase()
    const rawTokenAddress = (tokenAddress ?? '').trim()
    const normalized = compatibility === 'evm' ? rawTokenAddress.toLowerCase() : rawTokenAddress
    if (
      !normalized
      || rawTokenAddress.toLowerCase() === 'native'
      || rawTokenAddress.toLowerCase() === ethers.ZeroAddress.toLowerCase()
    ) {
      return {
        symbol: chainEntry?.nativeCurrency?.symbol ?? 'NATIVE',
        decimals: chainEntry?.nativeCurrency?.decimals ?? 18,
        isKnownToken: true,
      }
    }

    const meta = tokenMetadataMap.get(normalized)
    if (meta) {
      return {
        ...meta,
        isKnownToken: true,
      }
    }

    return {
      symbol: 'TOKEN',
      decimals: null,
      isKnownToken: false,
    }
  }

  const formatTokenOptionLabel = (tokenAddress: string): string => {
    const { symbol, isKnownToken } = resolveTokenMeta(tokenAddress)
    const addressLabel = truncateAddress(tokenAddress)
    if (!isKnownToken || !symbol || symbol === 'TOKEN') {
      return addressLabel
    }
    return `${addressLabel}（${symbol.toUpperCase()}）`
  }

  const withdrawTokenOptions = useMemo(() => {
    return tokens.map((item) => ({
      value: item.token,
      label: formatTokenOptionLabel(item.token),
    }))
  }, [chainRegistryRevision, chainEntry, tokens])

  const hasWithdrawTokens = withdrawTokenOptions.length > 0

  const getPreferredTreasuryToken = (tokenAddresses: string[]): string => {
    const stableToken = tokenAddresses.find((tokenAddress) => {
      const symbol = resolveTokenMeta(tokenAddress).symbol.toUpperCase()
      return symbol === 'USDC' || symbol === 'USDT'
    })
    return stableToken ?? tokenAddresses[0] ?? ''
  }

  const getDefaultTokenUnitAmount = (tokenAddress: string): string => {
    if (!tokenAddress) {
      return ''
    }
    const { decimals } = resolveTokenMeta(tokenAddress)
    if (decimals === null) {
      return ''
    }
    try {
      return (10n ** BigInt(decimals)).toString()
    } catch {
      return ''
    }
  }

  const formatAmount = (value: string | null | undefined, tokenAddress: string): string => {
    const raw = (value ?? '0').toString()
    if (!raw) {
      return '0'
    }
    try {
      const { symbol, decimals } = resolveTokenMeta(tokenAddress)
      if (decimals === null) {
        return raw
      }
      const human = formatUnits(BigInt(raw), decimals)
      const numeric = Number.parseFloat(human)
      const humanDisplay = Number.isFinite(numeric)
        ? numeric.toLocaleString(undefined, {
            maximumFractionDigits: Math.min(decimals, 6),
          })
        : human
      return `${raw} (${humanDisplay} ${symbol})`
    } catch (error) {
      return raw
    }
  }

  const formatTokenAmountPreview = (value: string, tokenAddress: string): string => {
    const raw = value.trim()
    if (!raw) {
      return '-'
    }
    if (!/^[0-9]+$/u.test(raw)) {
      return 'Invalid amount'
    }
    try {
      const { symbol, decimals } = resolveTokenMeta(tokenAddress)
      if (decimals === null) {
        return `${raw} base units`
      }
      const human = formatUnits(BigInt(raw), decimals)
      const numeric = Number.parseFloat(human)
      const humanDisplay = Number.isFinite(numeric)
        ? numeric.toLocaleString(undefined, {
            maximumFractionDigits: Math.min(decimals, 6),
          })
        : human
      return `${humanDisplay} ${symbol}`
    } catch {
      return '-'
    }
  }

  const withdrawAmountPreview = selectedToken
    ? formatTokenAmountPreview(withdrawAmount, selectedToken)
    : '-'

  useEffect(() => {
    return onChainRegistryUpdate(() => {
      setChainRegistryRevision(current => current + 1)
    })
  }, [])

  useEffect(() => {
    if (!selectedToken || withdrawAmount.trim()) {
      return
    }
    const defaultAmount = getDefaultTokenUnitAmount(selectedToken)
    if (defaultAmount) {
      setWithdrawAmount(defaultAmount)
    }
  }, [chainRegistryRevision, selectedToken, withdrawAmount])

  useEffect(() => {
    setBatchWithdrawItems(current => {
      let changed = false
      const updated = current.map(item => {
        if (!item.token || item.amount.trim()) {
          return item
        }
        const defaultAmount = getDefaultTokenUnitAmount(item.token)
        if (!defaultAmount) {
          return item
        }
        changed = true
        return {
          ...item,
          amount: defaultAmount,
        }
      })
      return changed ? updated : current
    })
  }, [chainRegistryRevision])

  const createBatchWithdrawItem = (tokenAddress: string): BatchWithdrawItemForm => ({
    id: `batch-item-${batchItemIdRef.current++}`,
    token: tokenAddress,
    amount: getDefaultTokenUnitAmount(tokenAddress),
  })

  const showActionError = (error: unknown) => {
    toast({
      title: 'Action failed',
      description: formatError(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    })
  }

  const showActionSuccess = (title: string, description: string) => {
    toast({
      title,
      description,
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    })
  }

  const syncPendingEvmOperation = useCallback((idempotencyKey: string) => {
    if (!user?.id || !requestMode || !canonicalChainRegistryId) {
      return
    }
    const hydrated = evmOperationService.hydratePending(user.id, requestMode)
    const resourceItems = hydrated.items.filter(item =>
      item.resourceType === 'fee_treasury'
      && item.resourceId === canonicalChainRegistryId
    )
    const pending = resourceItems.find(item => item.idempotencyKey === idempotencyKey)
      ?? resourceItems.at(-1)
      ?? null
    setPendingEvmOperation(pending)
    if (hydrated.quarantinedKeys.length > 0) {
      setHasQuarantinedEvmOperation(true)
    }
  }, [canonicalChainRegistryId, requestMode, user?.id])

  const reconcileEvmOperation = useCallback(async (item: EvmPendingOperationItem) => {
    if (!user?.id || !requestMode) {
      throw new Error('Admin identity or network mode is unavailable')
    }
    setIsRefreshingEvmOperation(true)
    try {
      const envelope = await evmOperationService.reconcilePending(user.id, requestMode, item)
      setEvmOperation(envelope)
      setEvmOperationError(null)
      syncPendingEvmOperation(item.idempotencyKey)
    } catch (error) {
      setEvmOperationError(formatError(error))
      throw error
    } finally {
      setIsRefreshingEvmOperation(false)
    }
  }, [requestMode, syncPendingEvmOperation, user?.id])

  const loadLatestEvmOperation = useCallback(async () => {
    if (!requestMode || !canonicalChainRegistryId) {
      throw new Error('Canonical EVM treasury resource identity is unavailable')
    }
    setIsRefreshingEvmOperation(true)
    try {
      const result = await evmOperationService.list(
        'fee_treasury',
        canonicalChainRegistryId,
        requestMode,
        0,
        100
      )
      const operation = result.items.at(-1) ?? null
      setEvmOperation(operation)
      setEvmOperationError(null)
      return operation
    } catch (error) {
      setEvmOperationError(formatError(error))
      throw error
    } finally {
      setIsRefreshingEvmOperation(false)
    }
  }, [canonicalChainRegistryId, requestMode])

  const refreshEvmOperation = useCallback(async () => {
    if (pendingEvmOperation) {
      return reconcileEvmOperation(pendingEvmOperation)
    }
    return loadLatestEvmOperation()
  }, [loadLatestEvmOperation, pendingEvmOperation, reconcileEvmOperation])

  const syncPendingTronOperation = useCallback((idempotencyKey: string) => {
    if (!user?.id || !requestMode || !canonicalChainRegistryId) {
      return
    }
    const hydrated = tronOperationService.hydratePending(user.id, requestMode)
    const resourceItems = hydrated.items.filter(item =>
      item.resourceId === canonicalChainRegistryId
    )
    const pending = resourceItems.find(item => item.idempotencyKey === idempotencyKey)
      ?? resourceItems.at(-1)
      ?? null
    setPendingTronOperation(pending)
    if (hydrated.quarantinedKeys.length > 0) {
      setHasQuarantinedTronOperation(true)
    }
  }, [canonicalChainRegistryId, requestMode, user?.id])

  const reconcileTronOperation = useCallback(async (item: TronPendingOperationItem) => {
    if (!user?.id || !requestMode) {
      throw new Error('Admin identity or network mode is unavailable')
    }
    setIsRefreshingTronOperation(true)
    try {
      const envelope = await tronOperationService.reconcilePending(
        user.id,
        requestMode,
        item
      )
      setTronOperation(envelope)
      setTronOperationError(null)
      syncPendingTronOperation(item.idempotencyKey)
      return envelope
    } catch (error) {
      setTronOperationError(formatError(error))
      throw error
    } finally {
      setIsRefreshingTronOperation(false)
    }
  }, [requestMode, syncPendingTronOperation, user?.id])

  const loadLatestTronOperation = useCallback(async () => {
    if (!requestMode || !canonicalChainRegistryId) {
      throw new Error('Canonical Tron treasury resource identity is unavailable')
    }
    setIsRefreshingTronOperation(true)
    try {
      const operation = await tronOperationService.latest(
        canonicalChainRegistryId,
        requestMode
      )
      setTronOperation(operation)
      setTronOperationError(null)
      return operation
    } catch (error) {
      setTronOperationError(formatError(error))
      throw error
    } finally {
      setIsRefreshingTronOperation(false)
    }
  }, [canonicalChainRegistryId, requestMode])

  const refreshTronOperation = useCallback(async () => {
    if (pendingTronOperation) {
      return reconcileTronOperation(pendingTronOperation)
    }
    return loadLatestTronOperation()
  }, [loadLatestTronOperation, pendingTronOperation, reconcileTronOperation])

  useEffect(() => {
    if (
      !isOpen
      || !isEvmTreasury
      || !canonicalChainRegistryId
      || pendingEvmOperation
      || !evmOperation
      || !EVM_TERMINAL_STATES.has(evmOperation.state)
    ) {
      return
    }
    void loadLatestEvmOperation().catch(() => undefined)
  }, [
    canonicalChainRegistryId,
    evmOperation,
    isEvmTreasury,
    isOpen,
    loadLatestEvmOperation,
    pendingEvmOperation,
  ])

  const handleCopyValue = async (value: string, successTitle: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast({
        title: successTitle,
        description: value,
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'top-right',
      })
    } catch (error) {
      showActionError(error)
    }
  }

  const loadTreasuryDetails = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (!overview) {
      return
    }

    if (mode === 'initial') {
      setIsLoading(true)
      setLoadError(null)
      setTokens([])
      setWhitelist(null)
      setWhitelistCount(0)
      setWhitelistWarning(null)
      setDiscrepancyList(null)
      setSelectedToken('')
      setWriteCapabilities(null)
      setCanonicalChainRegistryId(null)
    } else {
      setIsRefreshingData(true)
    }

    setWithdrawPreviewResult(null)
    setBatchWithdrawPreviewResult(null)

    try {
      const [
        tokensResponse,
        summaryResponse,
        capabilitiesResponse,
        canonicalOverviews,
      ] = await Promise.all([
        feeTreasuryService.getFeeTreasuryTokens(chainIdentifier, requestMode),
        feeTreasuryService.getFeeTreasurySummary(chainIdentifier, requestMode),
        (isSolanaTreasury || isWithdrawTabEnabled)
          ? feeTreasuryService.getWriteCapabilities(chainIdentifier, requestMode)
          : Promise.resolve(null),
        feeTreasuryService.getFeeTreasuryContracts(requestMode),
      ])

      const canonicalOverview = canonicalOverviews.find(item => item.id === overview.id)
      if ((isEvmTreasury || isTronTreasury) && !canonicalOverview?.chainRegistryId) {
        throw new Error('The canonical fee treasury resource ID is unavailable')
      }
      setCanonicalChainRegistryId(canonicalOverview?.chainRegistryId ?? null)

      const [whitelistResult, whitelistCountResult] = await Promise.allSettled([
        feeTreasuryService.getFeeTreasuryWhitelist(chainIdentifier, requestMode),
        feeTreasuryService.getFeeTreasuryWhitelistCount(chainIdentifier, requestMode),
      ])

      const uniqueTokens = Array.from(new Set<string>([
        ...(tokensResponse?.tokens ?? []),
        ...(summaryResponse?.tokens ?? []),
      ]))

      const tokenStats: FeeTreasuryTokenStats[] = await Promise.all(
        uniqueTokens.map(async (token) => {
          try {
            const stats = await feeTreasuryService.getFeeTreasuryTokenStats(
              chainIdentifier,
              token,
              requestMode
            )
            if (!isSolanaTreasury) {
              return stats
            }
            const discrepancy = await feeTreasuryService.getFeeTreasuryDiscrepancy(
              chainIdentifier,
              token,
              requestMode
            )
            return {
              ...stats,
              trackedBalance: discrepancy.trackedBalance,
              actualBalance: discrepancy.actualBalance,
              discrepancy: discrepancy.discrepancy,
              hasDiscrepancy: discrepancy.hasDiscrepancy,
            }
          } catch (error) {
            const tokenLabel = resolveTokenMeta(token).symbol.toUpperCase()
            throw new Error(`${tokenLabel}: ${formatError(error)}`)
          }
        }),
      )

      setLoadError(null)
      setTokens(tokenStats)
      if (whitelistResult.status === 'fulfilled') {
        setWhitelist(whitelistResult.value)
        setWhitelistWarning(whitelistResult.value.warning ?? null)
      } else {
        setWhitelistWarning(formatError(whitelistResult.reason))
      }
      if (whitelistCountResult.status === 'fulfilled') {
        setWhitelistCount(Number(whitelistCountResult.value.count ?? 0))
        if (whitelistResult.status === 'fulfilled' && !whitelistResult.value.warning) {
          setWhitelistWarning(whitelistCountResult.value.warning ?? null)
        }
      } else if (whitelistResult.status !== 'fulfilled') {
        setWhitelistCount(0)
      }
      if (capabilitiesResponse) {
        setWriteCapabilities(capabilitiesResponse)
      }
      const preferredToken = getPreferredTreasuryToken(uniqueTokens)
      setSelectedToken((current) => {
        if (current && uniqueTokens.includes(current)) {
          return current
        }
        return preferredToken
      })
      setWithdrawAmount((current) => {
        if (current.trim()) {
          return current
        }
        return getDefaultTokenUnitAmount(preferredToken)
      })
      setBatchWithdrawItems((current) => {
        if (current.length > 0) {
          return current.map((item) => {
            if (item.token && uniqueTokens.includes(item.token)) {
              return item
            }
            return {
              ...item,
              token: preferredToken,
              amount: item.amount.trim() || getDefaultTokenUnitAmount(preferredToken),
            }
          })
        }
        return preferredToken ? [createBatchWithdrawItem(preferredToken)] : []
      })
    } catch (error) {
      setLoadError(formatError(error))
    } finally {
      if (mode === 'initial') {
        setIsLoading(false)
      } else {
        setIsRefreshingData(false)
      }
    }
  }, [
    chainIdentifier,
    isEvmTreasury,
    isSolanaTreasury,
    isTronTreasury,
    isWithdrawTabEnabled,
    overview,
    requestMode,
  ])

  useEffect(() => {
    if (!isOpen || !overview) {
      return
    }

    void loadTreasuryDetails('initial')
  }, [isOpen, overview, loadTreasuryDetails])

  useEffect(() => {
    if (
      !isOpen
      || !isEvmTreasury
      || !canonicalChainRegistryId
      || !requestMode
      || !user?.id
    ) {
      return
    }
    try {
      const hydrated = evmOperationService.hydratePending(user.id, requestMode)
      const currentItems = hydrated.items.filter(item =>
        item.resourceType === 'fee_treasury'
        && item.resourceId === canonicalChainRegistryId
      )
      const latest = currentItems.at(-1) ?? null
      setPendingEvmOperation(latest)
      if (hydrated.quarantinedKeys.length > 0) {
        setHasQuarantinedEvmOperation(true)
      }
      if (latest) {
        void reconcileEvmOperation(latest).catch(() => undefined)
      } else {
        void loadLatestEvmOperation().catch(() => undefined)
      }
    } catch (error) {
      setEvmOperationError(formatError(error))
    }
  }, [
    isEvmTreasury,
    isOpen,
    canonicalChainRegistryId,
    loadLatestEvmOperation,
    reconcileEvmOperation,
    requestMode,
    user?.id,
  ])

  useEffect(() => {
    if (
      !isOpen
      || !isTronTreasury
      || !canonicalChainRegistryId
      || !requestMode
      || !user?.id
    ) {
      return
    }
    try {
      const hydrated = tronOperationService.hydratePending(user.id, requestMode)
      const currentItems = hydrated.items.filter(item =>
        item.resourceId === canonicalChainRegistryId
      )
      const latest = currentItems.at(-1) ?? null
      setPendingTronOperation(latest)
      if (hydrated.quarantinedKeys.length > 0) {
        setHasQuarantinedTronOperation(true)
      }
      if (latest) {
        void reconcileTronOperation(latest).catch(() => undefined)
      } else {
        void loadLatestTronOperation().catch(() => undefined)
      }
    } catch (error) {
      setTronOperationError(formatError(error))
    }
  }, [
    canonicalChainRegistryId,
    isOpen,
    isTronTreasury,
    loadLatestTronOperation,
    reconcileTronOperation,
    requestMode,
    user?.id,
  ])

  useEffect(() => {
    if (!isOpen) {
      setWithdrawExecutionResult(null)
      setBatchWithdrawResult(null)
      setWithdrawPreviewResult(null)
      setBatchWithdrawPreviewResult(null)
      setPendingConfirmAction(null)
      setPendingEvmOperation(null)
      setEvmOperation(null)
      setEvmOperationError(null)
      setHasQuarantinedEvmOperation(false)
      setPendingTronOperation(null)
      setTronOperation(null)
      setTronOperationError(null)
      setHasQuarantinedTronOperation(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setWithdrawAmount('')
    setRecipientAddress('')
    setBatchWithdrawItems([])
    setAuditReason('')
    setPendingConfirmAction(null)
    setPendingEvmOperation(null)
    setEvmOperation(null)
    setEvmOperationError(null)
    setHasQuarantinedEvmOperation(false)
    setPendingTronOperation(null)
    setTronOperation(null)
    setTronOperationError(null)
    setHasQuarantinedTronOperation(false)
  }, [isOpen, chainIdentifier])

  const handleRefreshDiscrepancy = async () => {
    if (!selectedToken) {
      return
    }
    setIsRefreshingDiscrepancy(true)
    try {
      const result = await feeTreasuryService.getFeeTreasuryDiscrepancy(
        chainIdentifier,
        selectedToken,
        requestMode
      )
      if (result.hasDiscrepancy) {
        toast({
          title: 'Discrepancy found',
          description: t('feeTreasury.details.results.discrepancy', {
            actual: formatAmount(result.actualBalance, selectedToken),
            tracked: formatAmount(result.trackedBalance, selectedToken),
            diff: formatAmount(result.discrepancy, selectedToken),
          }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
      } else {
        showActionSuccess('No discrepancy', t('feeTreasury.details.results.discrepancyZero'))
      }
    } catch (error) {
      showActionError(error)
    } finally {
      setIsRefreshingDiscrepancy(false)
    }
  }

  const handleRefreshTreasuryDetails = async () => {
    await loadTreasuryDetails('refresh')
  }
  const handleLoadDiscrepancyList = async () => {
    setIsLoadingDiscrepancyList(true)
    try {
      const result = await feeTreasuryService.getFeeTreasuryDiscrepancies(
        chainIdentifier,
        requestMode
      )
      const pairs: { token: string; difference: string }[] = (result.tokensWithDiscrepancy ?? []).map((token, index) => {
        const diff = result.discrepancies?.[index] ?? '0'
        return {
          token,
          difference: diff,
        }
      })
      setDiscrepancyList(pairs)
    } catch (error) {
      showActionError(error)
    } finally {
      setIsLoadingDiscrepancyList(false)
    }
  }

  const handleCheckWithdrawable = async () => {
    if (!selectedToken) {
      return
    }
    const amount = withdrawAmount?.trim()
    if (!amount) {
      showActionError('Amount is required')
      return
    }
    setIsCheckingWithdraw(true)
    try {
      const result = await feeTreasuryService.checkFeeTreasuryWithdrawable(
        chainIdentifier,
        selectedToken,
        amount,
        requestMode
      )
      if (result.canWithdraw) {
        showActionSuccess('Withdrawable check passed', t('feeTreasury.details.results.withdrawableTrue'))
      } else {
        toast({
          title: 'Withdrawable check failed',
          description: t('feeTreasury.details.results.withdrawableFalse'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
      }
    } catch (error) {
      showActionError(error)
    } finally {
      setIsCheckingWithdraw(false)
    }
  }

  const handleCheckCollector = async () => {
    const address = collectorAddress.trim()
    if (!address) {
      showActionError('Collector address is required')
      return
    }
    setIsCheckingCollector(true)
    try {
      const result = await feeTreasuryService.getFeeTreasuryCollectorStatus(
        chainIdentifier,
        address,
        requestMode
      )
      if (result.authorized) {
        showActionSuccess('Collector authorized', t('feeTreasury.details.results.collectorAuthorized'))
      } else {
        toast({
          title: 'Collector unauthorized',
          description: t('feeTreasury.details.results.collectorUnauthorized'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
      }
    } catch (error) {
      showActionError(error)
    } finally {
      setIsCheckingCollector(false)
    }
  }

  const handleCheckWhitelistStatus = async () => {
    const address = accountAddress.trim()
    if (!address) {
      showActionError('Address is required')
      return
    }
    setIsCheckingWhitelist(true)
    try {
      const result = await feeTreasuryService.getFeeTreasuryWhitelistStatus(
        chainIdentifier,
        address,
        requestMode
      )
      if (result.whitelisted) {
        showActionSuccess('Address whitelisted', t('feeTreasury.details.results.whitelisted'))
      } else {
        toast({
          title: 'Address not whitelisted',
          description: t('feeTreasury.details.results.notWhitelisted'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
      }
    } catch (error) {
      showActionError(error)
    } finally {
      setIsCheckingWhitelist(false)
    }
  }

  const parseBatchWithdrawals = (): Array<{ token: string; amount: string }> => {
    if (batchWithdrawItems.length === 0) {
      throw new Error('Add at least one batch withdrawal item')
    }

    return batchWithdrawItems.map((item, index) => {
      const token = item.token.trim()
      const amount = item.amount.trim()
      if (!token || !amount) {
        throw new Error(`Batch item ${index + 1} requires token and amount`)
      }
      return { token, amount }
    })
  }

  const handleAddBatchWithdrawItem = () => {
    const preferredToken = getPreferredTreasuryToken(withdrawTokenOptions.map(option => option.value))
    setBatchWithdrawItems((current) => [
      ...current,
      createBatchWithdrawItem(preferredToken),
    ])
  }

  const handleRemoveBatchWithdrawItem = (itemId: string) => {
    setBatchWithdrawItems((current) => current.filter(item => item.id !== itemId))
  }

  const handleBatchWithdrawTokenChange = (itemId: string, token: string) => {
    setBatchWithdrawItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? {
              ...item,
              token,
              amount: item.amount.trim() ? item.amount : getDefaultTokenUnitAmount(token),
            }
          : item
      )
    )
  }

  const handleBatchWithdrawAmountChange = (itemId: string, amount: string) => {
    setBatchWithdrawItems((current) =>
      current.map((item) => (item.id === itemId ? { ...item, amount } : item))
    )
  }

  const performExecuteWithdrawSolana = async () => {
    if (!isSolanaTreasury) {
      showActionError('Treasury write actions are only available for Solana')
      return
    }
    const address = recipientAddress.trim()
    const amount = withdrawAmount.trim()
    const reason = auditReason.trim()
    if (!selectedToken) {
      showActionError('Select a token before executing a withdraw')
      return
    }
    if (!address) {
      showActionError('Recipient address is required')
      return
    }
    if (!amount) {
      showActionError('Amount is required')
      return
    }
    if (reason.length < 3) {
      showActionError('Audit reason must be at least 3 characters')
      return
    }

    setIsExecutingWithdraw(true)
    setBatchWithdrawResult(null)
    try {
      const result = await feeTreasuryService.withdrawSolanaFee(
        chainIdentifier,
        selectedToken,
        address,
        amount,
        reason,
        requestMode
      )
      setWithdrawExecutionResult(result)
      showActionSuccess('Withdraw submitted', `Signature: ${result.signature}`)
    } catch (error) {
      showActionError(error)
    } finally {
      setIsExecutingWithdraw(false)
    }
  }

  const performExecuteBatchWithdrawSolana = async () => {
    if (!isSolanaTreasury) {
      showActionError('Treasury write actions are only available for Solana')
      return
    }
    const address = recipientAddress.trim()
    const reason = auditReason.trim()
    if (!address) {
      showActionError('Recipient address is required')
      return
    }
    if (reason.length < 3) {
      showActionError('Audit reason must be at least 3 characters')
      return
    }

    setIsExecutingBatchWithdraw(true)
    setWithdrawExecutionResult(null)
    try {
      const withdrawals = parseBatchWithdrawals()
      const result = await feeTreasuryService.batchWithdrawSolanaFee(
        chainIdentifier,
        address,
        withdrawals,
        reason,
        requestMode
      )
      setBatchWithdrawResult(result)
      showActionSuccess(
        'Batch withdraw submitted',
        `Signature: ${result.signature} · Items: ${result.itemCount}`,
      )
    } catch (error) {
      showActionError(error)
    } finally {
      setIsExecutingBatchWithdraw(false)
    }
  }

  const handleExecuteWithdraw = () => {
    if (!isSolanaTreasury) {
      showActionError('Treasury write actions are only available for Solana')
      return
    }
    if (!withdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a withdraw')
      return
    }
    setPendingConfirmAction('single_withdraw')
  }

  const handleExecuteBatchWithdraw = () => {
    if (!isSolanaTreasury) {
      showActionError('Treasury write actions are only available for Solana')
      return
    }
    if (!batchWithdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a batch withdraw')
      return
    }
    setPendingConfirmAction('batch_withdraw')
  }

  const handlePreviewWithdraw = async () => {
    if (!selectedToken) {
      showActionError('Select a token before previewing a withdraw')
      return
    }
    const address = recipientAddress.trim()
    const amount = withdrawAmount.trim()
    const reason = auditReason.trim()
    if (!address) {
      showActionError('Recipient address is required')
      return
    }
    if (!amount) {
      showActionError('Amount is required')
      return
    }
    if (reason.length < 3) {
      showActionError('Audit reason must be at least 3 characters')
      return
    }

    setIsPreviewingWithdraw(true)
    setWithdrawExecutionResult(null)
    try {
      const result = await feeTreasuryService.previewWithdrawFee(
        chainIdentifier,
        selectedToken,
        address,
        amount,
        reason,
        requestMode
      )
      setWithdrawPreviewResult(result)
    } catch (error) {
      showActionError(error)
    } finally {
      setIsPreviewingWithdraw(false)
    }
  }

  const performExecuteWithdrawGeneric = async () => {
    if (isSolanaTreasury) {
      return
    }
    const address = recipientAddress.trim()
    const amount = withdrawAmount.trim()
    const reason = auditReason.trim()
    if (!withdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a withdraw')
      return
    }

    let submittedEvmIdempotencyKey: string | null = null
    let submittedTronIdempotencyKey: string | null = null
    setIsExecutingWithdraw(true)
    try {
      if (isEvmTreasury) {
        if (!user?.id || !requestMode || !canonicalChainRegistryId) {
          throw new Error('EVM operation identity is unavailable')
        }
        if (hasNonterminalEvmOperation && !pendingEvmOperation) {
          throw new Error(
            'An EVM treasury operation was recovered from the server. Resolve it before starting a new action.'
          )
        }
        const expectedBody = { recipientAddress: address, amount, auditReason: reason }
        if (
          pendingEvmOperation
          && (
            pendingEvmOperation.operationType !== 'evm_fee_treasury_withdraw'
            || JSON.stringify(pendingEvmOperation.requestBody) !== JSON.stringify(expectedBody)
          )
        ) {
          throw new Error(
            'Another EVM treasury operation is still pending. Resolve it before starting a new action.'
          )
        }
        const pending = pendingEvmOperation
          ?? feeTreasuryService.createEvmWithdrawOperation(
            user.id,
            chainIdentifier,
            canonicalChainRegistryId,
            selectedToken,
            address,
            amount,
            reason,
            requestMode
          )
        submittedEvmIdempotencyKey = pending.idempotencyKey
        setPendingEvmOperation(pending)
        const operation = await feeTreasuryService.submitEvmOperation(
          user.id,
          pending,
          requestMode
        )
        setEvmOperation(operation)
        setEvmOperationError(null)
        syncPendingEvmOperation(pending.idempotencyKey)
        toast({
          title: `EVM operation ${operation.state}`,
          description: `Operation ID: ${operation.operationId}`,
          status: operation.state === 'rejected' || operation.state === 'failed'
            ? 'error'
            : operation.state === 'unknown'
              ? 'warning'
              : 'info',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        return
      }
      if (!isTronTreasury) {
        throw new Error('Unsupported treasury compatibility')
      }
      if (!user?.id || !requestMode || !canonicalChainRegistryId) {
        throw new Error('Tron operation identity is unavailable')
      }
      if (hasNonterminalTronOperation && !pendingTronOperation) {
        throw new Error(
          'A Tron treasury operation was recovered from the server. Resolve it before starting a new action.'
        )
      }
      const expectedBody = { recipientAddress: address, amount, auditReason: reason }
      if (
        pendingTronOperation
        && (
          pendingTronOperation.operationType !== 'tron_fee_treasury_withdraw'
          || JSON.stringify(pendingTronOperation.requestBody) !== JSON.stringify(expectedBody)
        )
      ) {
        throw new Error(
          'Another Tron treasury operation is still pending. Resolve it before starting a new action.'
        )
      }
      const pending = pendingTronOperation
        ?? feeTreasuryService.createTronWithdrawOperation(
          user.id,
          chainIdentifier,
          canonicalChainRegistryId,
          selectedToken,
          address,
          amount,
          reason,
          requestMode
        )
      submittedTronIdempotencyKey = pending.idempotencyKey
      setPendingTronOperation(pending)
      const operation = await feeTreasuryService.submitTronOperation(
        user.id,
        pending,
        requestMode
      )
      setTronOperation(operation)
      setTronOperationError(null)
      syncPendingTronOperation(pending.idempotencyKey)
      toast({
        title: `Tron operation ${operation.state}`,
        description: `Operation ID: ${operation.operationId}`,
        status: operation.state === 'rejected' || operation.state === 'failed'
          ? 'error'
          : operation.state === 'unknown'
            ? 'warning'
            : 'info',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    } catch (error) {
      if (submittedEvmIdempotencyKey) {
        try {
          syncPendingEvmOperation(submittedEvmIdempotencyKey)
        } catch (storageError) {
          setEvmOperationError(formatError(storageError))
        }
      }
      if (submittedTronIdempotencyKey) {
        try {
          syncPendingTronOperation(submittedTronIdempotencyKey)
        } catch (storageError) {
          setTronOperationError(formatError(storageError))
        }
      }
      showActionError(error)
    } finally {
      setIsExecutingWithdraw(false)
    }
  }

  const handlePreviewBatchWithdraw = async () => {
    const address = recipientAddress.trim()
    const reason = auditReason.trim()
    if (!address) {
      showActionError('Recipient address is required')
      return
    }
    if (reason.length < 3) {
      showActionError('Audit reason must be at least 3 characters')
      return
    }

    setIsPreviewingBatchWithdraw(true)
    setBatchWithdrawResult(null)
    try {
      const withdrawals = parseBatchWithdrawals()
      const result = await feeTreasuryService.previewBatchWithdrawFee(
        chainIdentifier,
        address,
        withdrawals,
        reason,
        requestMode
      )
      setBatchWithdrawPreviewResult(result)
    } catch (error) {
      showActionError(error)
    } finally {
      setIsPreviewingBatchWithdraw(false)
    }
  }

  const performExecuteBatchWithdrawGeneric = async () => {
    if (isSolanaTreasury) {
      return
    }
    const address = recipientAddress.trim()
    const reason = auditReason.trim()
    if (!batchWithdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a batch withdraw')
      return
    }

    let submittedEvmIdempotencyKey: string | null = null
    let submittedTronIdempotencyKey: string | null = null
    setIsExecutingBatchWithdraw(true)
    try {
      const withdrawals = parseBatchWithdrawals()
      if (isEvmTreasury) {
        if (!user?.id || !requestMode || !canonicalChainRegistryId) {
          throw new Error('EVM operation identity is unavailable')
        }
        if (hasNonterminalEvmOperation && !pendingEvmOperation) {
          throw new Error(
            'An EVM treasury operation was recovered from the server. Resolve it before starting a new action.'
          )
        }
        const expectedBody = { recipientAddress: address, withdrawals, auditReason: reason }
        if (
          pendingEvmOperation
          && (
            pendingEvmOperation.operationType !== 'evm_fee_treasury_batch_withdraw'
            || JSON.stringify(pendingEvmOperation.requestBody) !== JSON.stringify(expectedBody)
          )
        ) {
          throw new Error(
            'Another EVM treasury operation is still pending. Resolve it before starting a new action.'
          )
        }
        const pending = pendingEvmOperation
          ?? feeTreasuryService.createEvmBatchWithdrawOperation(
            user.id,
            chainIdentifier,
            canonicalChainRegistryId,
            address,
            withdrawals,
            reason,
            requestMode
          )
        submittedEvmIdempotencyKey = pending.idempotencyKey
        setPendingEvmOperation(pending)
        const operation = await feeTreasuryService.submitEvmOperation(
          user.id,
          pending,
          requestMode
        )
        setEvmOperation(operation)
        setEvmOperationError(null)
        syncPendingEvmOperation(pending.idempotencyKey)
        toast({
          title: `EVM operation ${operation.state}`,
          description: `Operation ID: ${operation.operationId}`,
          status: operation.state === 'rejected' || operation.state === 'failed'
            ? 'error'
            : operation.state === 'unknown'
              ? 'warning'
              : 'info',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        return
      }
      if (!isTronTreasury) {
        throw new Error('Unsupported treasury compatibility')
      }
      if (!user?.id || !requestMode || !canonicalChainRegistryId) {
        throw new Error('Tron operation identity is unavailable')
      }
      if (hasNonterminalTronOperation && !pendingTronOperation) {
        throw new Error(
          'A Tron treasury operation was recovered from the server. Resolve it before starting a new action.'
        )
      }
      const expectedBody = { recipientAddress: address, withdrawals, auditReason: reason }
      if (
        pendingTronOperation
        && (
          pendingTronOperation.operationType !== 'tron_fee_treasury_batch_withdraw'
          || JSON.stringify(pendingTronOperation.requestBody) !== JSON.stringify(expectedBody)
        )
      ) {
        throw new Error(
          'Another Tron treasury operation is still pending. Resolve it before starting a new action.'
        )
      }
      const pending = pendingTronOperation
        ?? feeTreasuryService.createTronBatchWithdrawOperation(
          user.id,
          chainIdentifier,
          canonicalChainRegistryId,
          address,
          withdrawals,
          reason,
          requestMode
        )
      submittedTronIdempotencyKey = pending.idempotencyKey
      setPendingTronOperation(pending)
      const operation = await feeTreasuryService.submitTronOperation(
        user.id,
        pending,
        requestMode
      )
      setTronOperation(operation)
      setTronOperationError(null)
      syncPendingTronOperation(pending.idempotencyKey)
      toast({
        title: `Tron operation ${operation.state}`,
        description: `Operation ID: ${operation.operationId}`,
        status: operation.state === 'rejected' || operation.state === 'failed'
          ? 'error'
          : operation.state === 'unknown'
            ? 'warning'
            : 'info',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    } catch (error) {
      if (submittedEvmIdempotencyKey) {
        try {
          syncPendingEvmOperation(submittedEvmIdempotencyKey)
        } catch (storageError) {
          setEvmOperationError(formatError(storageError))
        }
      }
      if (submittedTronIdempotencyKey) {
        try {
          syncPendingTronOperation(submittedTronIdempotencyKey)
        } catch (storageError) {
          setTronOperationError(formatError(storageError))
        }
      }
      showActionError(error)
    } finally {
      setIsExecutingBatchWithdraw(false)
    }
  }

  const handleExecuteWithdrawGeneric = () => {
    if (!withdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a withdraw')
      return
    }
    setPendingConfirmAction('single_withdraw')
  }

  const handleExecuteBatchWithdrawGeneric = () => {
    if (!batchWithdrawPreviewResult?.ready) {
      showActionError('Run preview before executing a batch withdraw')
      return
    }
    setPendingConfirmAction('batch_withdraw')
  }

  const handleConfirmExecute = async () => {
    const action = pendingConfirmAction
    setPendingConfirmAction(null)

    if (action === 'single_withdraw') {
      if (isSolanaTreasury) {
        await performExecuteWithdrawSolana()
        return
      }
      await performExecuteWithdrawGeneric()
      return
    }

    if (action === 'batch_withdraw') {
      if (isSolanaTreasury) {
        await performExecuteBatchWithdrawSolana()
        return
      }
      await performExecuteBatchWithdrawGeneric()
    }
  }

  const renderStatusBadge = (value: boolean, truthyLabel: string, falsyLabel: string) => (
    <Badge colorScheme={value ? 'green' : 'red'} fontSize="sm">
      {value ? truthyLabel : falsyLabel}
    </Badge>
  )

  const renderBatchItemsEditor = () => (
    <FormControl>
      <FormLabel fontSize="sm">Batch Items</FormLabel>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Box
          display={{ base: 'none', md: 'grid' }}
          gridTemplateColumns={batchItemsGridColumns.md}
          columnGap={3}
          mb={2}
          px={1}
        >
          <Text fontSize="sm" fontWeight="medium">
            Token
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            Amount
          </Text>
          <Box />
          <Box />
        </Box>
        <Stack spacing={2}>
          {batchWithdrawItems.map((item) => (
            <Box
              key={item.id}
              display="grid"
              gridTemplateColumns={batchItemsGridColumns}
              columnGap={3}
              rowGap={2}
              alignItems="center"
            >
              <Box w="full">
                <DropdownSelect
                  size="sm"
                  value={item.token}
                  options={withdrawTokenOptions}
                  onChange={(value) => handleBatchWithdrawTokenChange(item.id, value)}
                  placeholder="Select token"
                  isDisabled={!hasWithdrawTokens}
                />
              </Box>
              <Input
                size="sm"
                value={item.amount}
                onChange={(event) =>
                  handleBatchWithdrawAmountChange(item.id, event.target.value)
                }
                placeholder="Enter amount"
              />
              <Box
                minW={{ base: '112px', md: '132px' }}
                w="full"
                px={3}
                borderWidth="1px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={amountPreviewBg}
                borderColor={amountPreviewBorderColor}
                color={amountPreviewColor}
              >
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {item.token ? formatTokenAmountPreview(item.amount, item.token) : '-'}
                </Text>
              </Box>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleRemoveBatchWithdrawItem(item.id)}
                isDisabled={batchWithdrawItems.length === 1}
                alignSelf={{ base: 'flex-end', md: 'center' }}
                justifySelf={{ base: 'flex-end', md: 'stretch' }}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Stack>
      </Box>
      <Button
        size="sm"
        variant="outline"
        mt={2}
        alignSelf="flex-start"
        onClick={handleAddBatchWithdrawItem}
        isDisabled={!hasWithdrawTokens}
      >
        Add Item
      </Button>
    </FormControl>
  )

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflow="hidden">
          <ModalHeader>
            {overview
              ? t('feeTreasury.details.title', { chain: overview.chainName || overview.chainKey })
              : t('feeTreasury.title')}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pt={0} pb={4}>
            <Tabs variant="enclosed" colorScheme="blue" size="sm">
              <Box
                position="sticky"
                top={0}
                zIndex={2}
                bg={stickyBg}
                pt={4}
                pb={3}
                mb={4}
                borderBottomWidth="1px"
                borderColor={stickyBorderColor}
              >
                {overview && (
                  <Stack spacing={3}>
                    <HStack justify="space-between" align="center" spacing={4} w="full">
                      <HStack spacing={3} wrap="wrap" align="center" flex="1" minW={0}>
                        <Badge colorScheme={overview.isTestnet ? 'purple' : 'blue'}>
                          {overview.isTestnet ? t('feeTreasury.envTestnet') : t('feeTreasury.envMainnet')}
                        </Badge>
                        {renderStatusBadge(
                          overview.isActive,
                          t('feeTreasury.statusActive'),
                          t('feeTreasury.statusInactive'),
                        )}
                        <Badge>{overview.version}</Badge>
                        <HStack spacing={2} align="center" minW={0}>
                          <Text fontWeight="semibold">{t('feeTreasury.table.address')}:</Text>
                          <Text fontFamily="mono" fontSize="sm">
                            {`${overview.contractAddress.slice(0, 6)}...${overview.contractAddress.slice(-6)}`}
                          </Text>
                          <Tooltip label={hasCopied ? 'Copied' : 'Copy'}>
                            <IconButton
                              aria-label="copy-address"
                              icon={<FiCopy />}
                              size="xs"
                              variant="ghost"
                              colorScheme={hasCopied ? 'green' : 'gray'}
                              onClick={onCopy}
                            />
                          </Tooltip>
                          {explorerUrl && (
                            <Tooltip label="Open in explorer">
                              <IconButton
                                aria-label="open-explorer"
                                icon={<FiExternalLink />}
                                size="xs"
                                variant="ghost"
                                as="a"
                                href={explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </HStack>
                      <HStack spacing={2} align="center" flexShrink={0}>
                        <TabList
                          mb={0}
                          p={1}
                          borderWidth="1px"
                          borderColor={tabListBorderColor}
                          borderRadius="xl"
                          bg={tabListBg}
                          overflow="visible"
                          gap={1}
                        >
                          <Tab
                            px={4}
                            py={2}
                            fontSize="sm"
                            color={tabColor}
                            borderWidth="1px"
                            borderColor="transparent"
                            borderRadius="lg"
                            _hover={{
                              bg: tabHoverBg,
                              color: tabHoverColor,
                            }}
                            _selected={{
                              bg: tabSelectedBg,
                              borderColor: tabSelectedBorderColor,
                              color: tabSelectedColor,
                              boxShadow: tabSelectedShadow,
                            }}
                            _disabled={{
                              color: tabDisabledColor,
                              cursor: 'not-allowed',
                              opacity: 0.7,
                            }}
                          >
                            Checks
                          </Tab>
                          <Tab
                            px={4}
                            py={2}
                            fontSize="sm"
                            color={tabColor}
                            borderWidth="1px"
                            borderColor="transparent"
                            borderRadius="lg"
                            _hover={{
                              bg: tabHoverBg,
                              color: tabHoverColor,
                            }}
                            _selected={{
                              bg: tabSelectedBg,
                              borderColor: tabSelectedBorderColor,
                              color: tabSelectedColor,
                              boxShadow: tabSelectedShadow,
                            }}
                            _disabled={{
                              color: tabDisabledColor,
                              cursor: 'not-allowed',
                              opacity: 0.7,
                            }}
                            isDisabled={!isSolanaTreasury && !isWithdrawTabEnabled}
                          >
                            Withdraw
                          </Tab>
                        </TabList>
                        <RefreshIconButton
                          label={t('feeTreasury.details.buttons.refreshDetails')}
                          onClick={() => {
                            void handleRefreshTreasuryDetails()
                          }}
                          isLoading={isRefreshingData}
                          flexShrink={0}
                        />
                      </HStack>
                    </HStack>
                    {isEvmTreasury && (
                      pendingEvmOperation
                      || evmOperation
                      || evmOperationError
                      || hasQuarantinedEvmOperation
                    ) && (
                      <Alert
                        status={hasQuarantinedEvmOperation
                          ? 'warning'
                          : evmOperation?.state === 'failed' || evmOperation?.state === 'rejected'
                          ? 'error'
                          : evmOperation?.state === 'unknown' || evmOperationError
                            ? 'warning'
                            : 'info'}
                        borderRadius="md"
                      >
                        <AlertIcon />
                        <Box flex="1">
                          <Text fontWeight="semibold">
                            EVM execution operation: {evmOperation?.state ?? 'pending'}
                          </Text>
                          <Text fontSize="sm">
                            {hasQuarantinedEvmOperation
                              ? 'A stored EVM operation is invalid and has been isolated. It was not submitted.'
                              : evmOperationError
                              ?? (evmOperation
                                ? `Operation ${evmOperation.operationId}. This execution state is separate from treasury business data.`
                                : 'The original request identity is persisted and will be reused.')}
                          </Text>
                        </Box>
                        {(pendingEvmOperation || hasNonterminalEvmOperation || evmOperationError) && (
                          <Button
                            size="sm"
                            ml={3}
                            variant="outline"
                            isLoading={isRefreshingEvmOperation}
                            onClick={() => {
                              void refreshEvmOperation().catch(() => undefined)
                            }}
                          >
                            Refresh operation
                          </Button>
                        )}
                      </Alert>
                    )}
                    {isTronTreasury && (
                      pendingTronOperation
                      || tronOperation
                      || tronOperationError
                      || hasQuarantinedTronOperation
                    ) && (
                      <Alert
                        status={hasQuarantinedTronOperation
                          ? 'warning'
                          : tronOperation?.state === 'failed'
                            || tronOperation?.state === 'rejected'
                            ? 'error'
                            : tronOperation?.state === 'unknown' || tronOperationError
                              ? 'warning'
                              : 'info'}
                        borderRadius="md"
                      >
                        <AlertIcon />
                        <Box flex="1">
                          <Text fontWeight="semibold">
                            Tron execution operation: {tronOperation?.state ?? 'pending recovery'}
                          </Text>
                          <Text fontSize="sm">
                            {hasQuarantinedTronOperation
                              ? 'A stored Tron operation is invalid and has been isolated. It was not submitted.'
                              : tronOperationError
                              ?? (tronOperation
                                ? `Operation ${tronOperation.operationId}. This execution state is separate from treasury business data.`
                                : 'The original request identity is persisted and will be reused.')}
                          </Text>
                        </Box>
                        {(
                          pendingTronOperation
                          || hasNonterminalTronOperation
                          || tronOperationError
                        ) && (
                          <Button
                            size="sm"
                            ml={3}
                            variant="outline"
                            isLoading={isRefreshingTronOperation}
                            onClick={() => {
                              void refreshTronOperation().catch(() => undefined)
                            }}
                          >
                            Refresh operation
                          </Button>
                        )}
                      </Alert>
                    )}
                  </Stack>
                )}

                {loadError && (
                  <Alert status="error" mt={3}>
                    <AlertIcon />
                    {loadError}
                  </Alert>
                )}
              </Box>

              {isLoading ? (
              <Stack align="center" py={10}>
                <Spinner size="lg" />
                <Text color="gray.500">{t('feeTreasury.states.loading')}</Text>
              </Stack>
              ) : (
              <>
                {tokens.length === 0 && (
                  <Text color="gray.500" mb={6}>
                    {t('feeTreasury.states.empty')}
                  </Text>
                )}

                <Box mt={4}>
                  <TabPanels>
                    <TabPanel px={0} pt={4}>
                      <Stack spacing={5}>
                        {tokens.length > 0 && (
                          <Box overflowX="auto">
                            <Text fontWeight="semibold" mb={2}>
                              {t('feeTreasury.details.summaryTitle')}
                            </Text>
                            <Table size="xs" variant="striped">
                              <Thead>
                                <Tr>
                                  <Th fontSize="xs">{t('feeTreasury.details.summaryHeaders.token')}</Th>
                                  <Th fontSize="xs">{t('feeTreasury.details.summaryHeaders.totalCollected')}</Th>
                                  <Th fontSize="xs">{t('feeTreasury.details.summaryHeaders.totalWithdrawn')}</Th>
                                  {isSolanaTreasury && (
                                    <Th fontSize="xs">{t('feeTreasury.details.summaryHeaders.trackedBalance')}</Th>
                                  )}
                                  {isSolanaTreasury && (
                                    <Th fontSize="xs">{t('feeTreasury.details.summaryHeaders.actualBalance')}</Th>
                                  )}
                                  <Th fontSize="xs">
                                    {isSolanaTreasury
                                      ? t('feeTreasury.details.summaryHeaders.executableBalance')
                                      : t('feeTreasury.details.summaryHeaders.availableBalance')}
                                  </Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {tokens.map((item) => (
                                  <Tr key={item.token}>
                                    <Td fontFamily="mono" fontSize="xs">{item.token}</Td>
                                    <Td fontSize="xs">{formatAmount(item.totalCollected, item.token)}</Td>
                                    <Td fontSize="xs">{formatAmount(item.totalWithdrawn, item.token)}</Td>
                                    {isSolanaTreasury && (
                                      <Td fontSize="xs">{formatAmount(item.trackedBalance ?? '0', item.token)}</Td>
                                    )}
                                    {isSolanaTreasury && (
                                      <Td fontSize="xs">{formatAmount(item.actualBalance ?? '0', item.token)}</Td>
                                    )}
                                    <Td fontSize="xs">{formatAmount(item.availableBalance, item.token)}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        )}

                        <Stack direction={{ base: 'column', md: 'row' }} spacing={5}>
                          <Box flex="1">
                            <Text fontWeight="semibold" mb={2}>
                              {t('feeTreasury.details.discrepancyTitle')}
                            </Text>
                            <Text fontSize="sm" color="gray.500" mb={3}>
                              {t('feeTreasury.details.discrepancyDescription')}
                            </Text>
                            <Stack spacing={3}>
                              <FormControl>
                                <DropdownSelect
                                  size="sm"
                                  value={selectedToken}
                                  options={tokens.map(item => ({
                                    value: item.token,
                                    label: item.token,
                                  }))}
                                  onChange={setSelectedToken}
                                  ariaLabel={t('feeTreasury.details.summaryHeaders.token')}
                                />
                              </FormControl>
                              <HStack spacing={3} align="stretch">
                                <Button
                                  size="sm"
                                  leftIcon={<FiRefreshCw />}
                                  onClick={handleRefreshDiscrepancy}
                                  isLoading={isRefreshingDiscrepancy}
                                  isDisabled={!selectedToken}
                                  flex="1"
                                >
                                  Check selected token
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleLoadDiscrepancyList}
                                  isLoading={isLoadingDiscrepancyList}
                                  flex="1"
                                >
                                  {t('feeTreasury.details.buttons.loadDiscrepancies')}
                                </Button>
                              </HStack>
                              {discrepancyList && (
                                <Box borderWidth="1px" borderRadius="md" p={3}>
                                  <Text fontWeight="semibold" mb={2}>
                                    {t('feeTreasury.details.discrepancyBulkTitle')}
                                  </Text>
                                  {discrepancyList.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">
                                      {t('feeTreasury.details.discrepancyBulkEmpty')}
                                    </Text>
                                  ) : (
                                    <VStack spacing={2} align="stretch">
                                      {discrepancyList.map(item => (
                                        <HStack key={item.token} justify="space-between">
                                          <Text fontFamily="mono" fontSize="sm">
                                            {item.token}
                                          </Text>
                                          <Badge colorScheme="yellow" fontSize="xs">
                                            {formatAmount(item.difference, item.token)}
                                          </Badge>
                                        </HStack>
                                      ))}
                                    </VStack>
                                  )}
                                </Box>
                              )}
                            </Stack>
                          </Box>

                          <Box flex="1">
                            <Text fontWeight="semibold" mb={2}>
                              {t('feeTreasury.details.whitelistTitle')}
                            </Text>
                            <Text fontSize="sm" color="gray.500" mb={3}>
                              {t('feeTreasury.details.whitelistCount', { count: whitelistCount })}
                            </Text>
                            {whitelistWarning && (
                              <Alert status="warning" mb={3}>
                                <AlertIcon />
                                {whitelistWarning}
                              </Alert>
                            )}
                            <VStack align="stretch" spacing={2} borderWidth="1px" borderRadius="md" p={3}>
                              {whitelist?.addresses?.length ? (
                                whitelist.addresses.map((address) => (
                                  <HStack
                                    key={address}
                                    justify="space-between"
                                    spacing={2}
                                    px={1}
                                    py={1.5}
                                  >
                                    <Tooltip label={address}>
                                      <Text fontFamily="mono" fontSize="sm" noOfLines={1} flex="1">
                                        {address}
                                      </Text>
                                    </Tooltip>
                                    <HStack spacing={1} flexShrink={0}>
                                      <Tooltip label="Copy address">
                                        <IconButton
                                          aria-label={`copy-whitelist-address-${address}`}
                                          icon={<FiCopy />}
                                          size="xs"
                                          variant="ghost"
                                          onClick={() => {
                                            void handleCopyValue(address, 'Address copied')
                                          }}
                                        />
                                      </Tooltip>
                                      {overview && (
                                        <Tooltip label="Open in explorer">
                                          <IconButton
                                            aria-label={`open-whitelist-address-${address}`}
                                            icon={<FiExternalLink />}
                                            size="xs"
                                            variant="ghost"
                                            as="a"
                                            href={getExplorerAddressUrl(overview.chainId, address)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          />
                                        </Tooltip>
                                      )}
                                    </HStack>
                                  </HStack>
                                ))
                              ) : (
                                <Text color="gray.500" fontSize="sm">
                                  {t('feeTreasury.details.whitelistEmpty')}
                                </Text>
                              )}
                            </VStack>
                          </Box>
                        </Stack>

                        <Stack spacing={4}>
                          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                              <FormControl>
                                <FormLabel fontSize="sm">Amount</FormLabel>
                                <HStack spacing={3} align="stretch">
                                  <Input
                                    size="sm"
                                    value={withdrawAmount}
                                    onChange={(event) => setWithdrawAmount(event.target.value)}
                                    placeholder="Enter amount"
                                  />
                                  <Box
                                    minW={{ base: '112px', md: '132px' }}
                                    px={3}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bg={amountPreviewBg}
                                    borderColor={amountPreviewBorderColor}
                                    color={amountPreviewColor}
                                  >
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                      {withdrawAmountPreview}
                                    </Text>
                                  </Box>
                                </HStack>
                              </FormControl>
                            <Button
                              size="sm"
                              alignSelf={{ base: 'stretch', md: 'flex-end' }}
                              onClick={handleCheckWithdrawable}
                              isLoading={isCheckingWithdraw}
                              isDisabled={!selectedToken}
                            >
                              {t('feeTreasury.details.buttons.checkWithdraw')}
                            </Button>
                          </Stack>
                          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">
                                {t('feeTreasury.details.inputs.collectorLabel')}
                              </FormLabel>
                              <Input
                                size="sm"
                                value={collectorAddress}
                                onChange={(event) => setCollectorAddress(event.target.value)}
                                placeholder={compatibilityHint}
                              />
                            </FormControl>
                            <Button
                              size="sm"
                              alignSelf={{ base: 'stretch', md: 'flex-end' }}
                              onClick={handleCheckCollector}
                              isLoading={isCheckingCollector}
                            >
                              {t('feeTreasury.details.buttons.checkCollector')}
                            </Button>
                          </Stack>
                          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">
                                {t('feeTreasury.details.inputs.accountLabel')}
                              </FormLabel>
                              <Input
                                size="sm"
                                value={accountAddress}
                                onChange={(event) => setAccountAddress(event.target.value)}
                                placeholder={compatibilityHint}
                              />
                            </FormControl>
                            <Button
                              size="sm"
                              alignSelf={{ base: 'stretch', md: 'flex-end' }}
                              onClick={handleCheckWhitelistStatus}
                              isLoading={isCheckingWhitelist}
                            >
                              {t('feeTreasury.details.buttons.checkWhitelist')}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </TabPanel>

                    <TabPanel px={0} pt={4}>
                      <Box pr={1}>
                      {isSolanaTreasury ? (
                        <Stack spacing={3}>
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={2}>
                              <Text fontWeight="semibold">Write Capabilities</Text>
                              <Text fontSize="sm" color="gray.500">
                                Solana withdrawals use the deployment signer, enforce the current
                                whitelist, and create the recipient ATA on demand when needed.
                              </Text>
                              {writeCapabilities ? (
                                <>
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Badge colorScheme={writeCapabilities.writeEnabled ? 'green' : 'yellow'}>
                                      {writeCapabilities.writeEnabled ? 'Write enabled' : 'Write disabled'}
                                    </Badge>
                                    <Badge colorScheme={writeCapabilities.signerAuthorized ? 'green' : 'red'}>
                                      {writeCapabilities.signerAuthorized ? 'Signer authorized' : 'Signer unauthorized'}
                                    </Badge>
                                    <Badge colorScheme={hasWhitelistRecipients ? 'green' : 'yellow'}>
                                      {hasWhitelistRecipients
                                        ? `Whitelisted recipients: ${whitelistOptions.length}`
                                        : 'No whitelisted recipient'}
                                    </Badge>
                                    <Badge colorScheme="blue">ATA auto-ensure enabled</Badge>
                                  </HStack>
                                  <Stack spacing={1} fontSize="sm">
                                    <Text fontFamily="mono">Signer: {writeCapabilities.signerAddress}</Text>
                                    {writeCapabilities.ownerAddress && (
                                      <Text fontFamily="mono">Deployment: {writeCapabilities.ownerAddress}</Text>
                                    )}
                                    {withdrawalSignerDisplay && (
                                      <Text fontFamily="mono">
                                        Withdrawal signer: {withdrawalSignerDisplay}
                                      </Text>
                                    )}
                                  </Stack>
                                </>
                              ) : (
                                <Text fontSize="sm" color="gray.500">
                                  Loading write capabilities...
                                </Text>
                              )}
                            </Stack>
                          </Box>

                          {!writeCapabilities?.writeEnabled && (
                            <Alert status="warning">
                              <AlertIcon />
                              Withdraw is unavailable because the current deployment signer does not
                              match the on-chain platform config.
                            </Alert>
                          )}

                          {!hasWhitelistRecipients && (
                            <Alert status="warning">
                              <AlertIcon />
                              No enumerable whitelisted recipient is currently available for this
                              treasury. Enter a recipient manually; preview verifies whitelist
                              status on-chain.
                            </Alert>
                          )}

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Audit Reason</Text>
                              <FormControl isRequired>
                                <Textarea
                                  size="sm"
                                  minH="72px"
                                  value={auditReason}
                                  onChange={(event) => setAuditReason(event.target.value)}
                                  placeholder="Why is this Solana treasury withdraw needed?"
                                />
                                <FormHelperText fontSize="xs">
                                  Required for Solana preview and execution. Minimum 3 characters.
                                </FormHelperText>
                              </FormControl>
                            </Stack>
                          </Box>

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Solana Withdraw</Text>
                              <Text fontSize="sm" color="gray.500">
                                Recipient must already be present in the withdrawal whitelist.
                              </Text>
                              <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
                                <FormControl>
                                  <FormLabel fontSize="sm">Token</FormLabel>
                                  <DropdownSelect
                                    size="sm"
                                    value={selectedToken}
                                    options={withdrawTokenOptions}
                                    onChange={setSelectedToken}
                                    placeholder="Select token"
                                    isDisabled={!hasWithdrawTokens}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontSize="sm">Recipient</FormLabel>
                                  {hasWhitelistRecipients ? (
                                    <DropdownSelect
                                      size="sm"
                                      value={recipientAddress}
                                      options={whitelistOptions}
                                      onChange={setRecipientAddress}
                                      placeholder="Select whitelisted recipient"
                                    />
                                  ) : (
                                    <Input
                                      size="sm"
                                      value={recipientAddress}
                                      onChange={(event) => setRecipientAddress(event.target.value)}
                                      placeholder={compatibilityHint}
                                    />
                                  )}
                                  <FormHelperText fontSize="xs">
                                    {hasWhitelistRecipients
                                      ? 'Recipient comes from the current withdrawal whitelist.'
                                      : 'Preview verifies the manually entered recipient on-chain.'}
                                  </FormHelperText>
                                </FormControl>
                              </Stack>
                              <FormControl>
                                <FormLabel fontSize="sm">Amount</FormLabel>
                                <HStack spacing={3} align="stretch">
                                  <Input
                                    size="sm"
                                    value={withdrawAmount}
                                    onChange={(event) => setWithdrawAmount(event.target.value)}
                                    placeholder="Enter amount"
                                  />
                                  <Box
                                    minW={{ base: '112px', md: '132px' }}
                                    px={3}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bg={amountPreviewBg}
                                    borderColor={amountPreviewBorderColor}
                                    color={amountPreviewColor}
                                  >
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                      {withdrawAmountPreview}
                                    </Text>
                                  </Box>
                                </HStack>
                              </FormControl>
                              <HStack spacing={3} flexWrap="wrap" justify="flex-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handlePreviewWithdraw}
                                  isLoading={isPreviewingWithdraw}
                                  isDisabled={
                                    !selectedToken
                                    || !recipientAddress
                                    || auditReason.trim().length < 3
                                    || !writeCapabilities?.writeEnabled
                                  }
                                >
                                  Preview Withdraw
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleExecuteWithdraw}
                                  isLoading={isExecutingWithdraw}
                                  isDisabled={
                                    !withdrawPreviewResult?.ready
                                    || !writeCapabilities?.writeEnabled
                                    || (isEvmTreasury && (
                                      pendingEvmOperation !== null
                                      || hasNonterminalEvmOperation
                                      || isRefreshingEvmOperation
                                    ))
                                    || (isTronTreasury && (
                                      pendingTronOperation !== null
                                      || hasNonterminalTronOperation
                                      || isRefreshingTronOperation
                                      || tronOperationError !== null
                                    ))
                                  }
                                >
                                  Execute Withdraw
                                </Button>
                              </HStack>
                              {withdrawPreviewResult && (
                                <Alert
                                  status={withdrawPreviewResult.ready ? 'success' : 'warning'}
                                  flexDirection="column"
                                  alignItems="flex-start"
                                >
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={1}>
                                      <Text fontWeight="semibold">Preview</Text>
                                      <Text>Ready: {withdrawPreviewResult.ready ? 'yes' : 'no'}</Text>
                                      <Text>Token tracked: {withdrawPreviewResult.tokenTracked ? 'yes' : 'no'}</Text>
                                      <Text>
                                        Recipient whitelisted:{' '}
                                        {withdrawPreviewResult.recipientWhitelisted ? 'yes' : 'no'}
                                      </Text>
                                      <Text>Can withdraw: {withdrawPreviewResult.canWithdraw ? 'yes' : 'no'}</Text>
                                      <Text>
                                        Signer authorized: {withdrawPreviewResult.signerAuthorized ? 'yes' : 'no'}
                                      </Text>
                                      {withdrawPreviewResult.recipientTokenAccount && (
                                        <Text fontSize="sm" fontFamily="mono">
                                          Recipient ATA: {withdrawPreviewResult.recipientTokenAccount}
                                        </Text>
                                      )}
                                      <Text>
                                        ATA exists: {withdrawPreviewResult.recipientTokenAccountExists ? 'yes' : 'no'}
                                      </Text>
                                      <Text>
                                        Will create ATA:{' '}
                                        {withdrawPreviewResult.willCreateRecipientTokenAccount ? 'yes' : 'no'}
                                      </Text>
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                              {withdrawExecutionResult && (
                                <Alert status="success" flexDirection="column" alignItems="flex-start">
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={1}>
                                      <Text fontWeight="semibold">Withdraw submitted</Text>
                                      <Text fontSize="sm" fontFamily="mono">
                                        Signature: {withdrawExecutionResult.signature}
                                      </Text>
                                      <Text fontSize="sm" fontFamily="mono">
                                        Recipient ATA: {withdrawExecutionResult.recipientTokenAccount}
                                      </Text>
                                      <Text fontSize="sm">
                                        ATA created: {withdrawExecutionResult.recipientTokenAccountCreated ? 'yes' : 'no'}
                                      </Text>
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                            </Stack>
                          </Box>

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Solana Batch Withdraw</Text>
                              <FormControl>
                                <FormLabel fontSize="sm">Recipient</FormLabel>
                                {hasWhitelistRecipients ? (
                                  <DropdownSelect
                                    size="sm"
                                    value={recipientAddress}
                                    options={whitelistOptions}
                                    onChange={setRecipientAddress}
                                    placeholder="Select whitelisted recipient"
                                  />
                                ) : (
                                  <Input
                                    size="sm"
                                    value={recipientAddress}
                                    onChange={(event) => setRecipientAddress(event.target.value)}
                                    placeholder={compatibilityHint}
                                  />
                                )}
                                <FormHelperText fontSize="xs">
                                  {hasWhitelistRecipients
                                    ? 'Batch withdraw uses the same whitelisted recipient for every token.'
                                    : 'Preview verifies the manually entered recipient on-chain.'}
                                </FormHelperText>
                              </FormControl>
                              {renderBatchItemsEditor()}
                              <HStack spacing={3} flexWrap="wrap" justify="flex-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handlePreviewBatchWithdraw}
                                  isLoading={isPreviewingBatchWithdraw}
                                  isDisabled={
                                    !recipientAddress
                                    || auditReason.trim().length < 3
                                    || !writeCapabilities?.writeEnabled
                                  }
                                >
                                  Preview Batch Withdraw
                                </Button>
                                <Button
                                  size="sm"
                                  alignSelf={{ base: 'stretch', md: 'flex-start' }}
                                  onClick={handleExecuteBatchWithdraw}
                                  isLoading={isExecutingBatchWithdraw}
                                  isDisabled={
                                    !batchWithdrawPreviewResult?.ready
                                    || !writeCapabilities?.writeEnabled
                                    || (isEvmTreasury && (
                                      pendingEvmOperation !== null
                                      || hasNonterminalEvmOperation
                                      || isRefreshingEvmOperation
                                    ))
                                    || (isTronTreasury && (
                                      pendingTronOperation !== null
                                      || hasNonterminalTronOperation
                                      || isRefreshingTronOperation
                                      || tronOperationError !== null
                                    ))
                                  }
                                >
                                  Execute Batch Withdraw
                                </Button>
                              </HStack>
                              {batchWithdrawPreviewResult && (
                                <Alert
                                  status={batchWithdrawPreviewResult.ready ? 'success' : 'warning'}
                                  flexDirection="column"
                                  alignItems="flex-start"
                                >
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={1} width="full">
                                      <Text fontWeight="semibold">Batch Preview</Text>
                                      <Text>
                                        Recipient whitelisted:{' '}
                                        {batchWithdrawPreviewResult.recipientWhitelisted ? 'yes' : 'no'}
                                      </Text>
                                      <Text>
                                        Signer authorized:{' '}
                                        {batchWithdrawPreviewResult.signerAuthorized ? 'yes' : 'no'}
                                      </Text>
                                      <VStack align="stretch" spacing={2}>
                                        {batchWithdrawPreviewResult.items.map((item) => (
                                          <Box key={`${item.token}:${item.amount}`} borderWidth="1px" borderRadius="md" p={2}>
                                            <Text fontFamily="mono" fontSize="sm">
                                              {item.token}
                                            </Text>
                                            <Text fontSize="sm">Amount: {item.amount}</Text>
                                            <Text fontSize="sm">
                                              Token tracked: {item.tokenTracked ? 'yes' : 'no'}
                                            </Text>
                                            <Text fontSize="sm">
                                              Can withdraw: {item.canWithdraw ? 'yes' : 'no'}
                                            </Text>
                                            {item.recipientTokenAccount && (
                                              <Text fontSize="sm" fontFamily="mono">
                                                ATA: {item.recipientTokenAccount}
                                              </Text>
                                            )}
                                            <Text fontSize="sm">
                                              ATA exists: {item.recipientTokenAccountExists ? 'yes' : 'no'}
                                            </Text>
                                            <Text fontSize="sm">
                                              Will create ATA: {item.willCreateRecipientTokenAccount ? 'yes' : 'no'}
                                            </Text>
                                          </Box>
                                        ))}
                                      </VStack>
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                              {batchWithdrawResult && (
                                <Alert status="success" flexDirection="column" alignItems="flex-start">
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={1} width="full">
                                      <Text fontWeight="semibold">Batch withdraw submitted</Text>
                                      <Text fontSize="sm" fontFamily="mono">
                                        Signature: {batchWithdrawResult.signature}
                                      </Text>
                                      <Text fontSize="sm">
                                        Item count: {batchWithdrawResult.itemCount}
                                      </Text>
                                      <VStack align="stretch" spacing={1}>
                                        {batchWithdrawResult.withdrawals.map(item => (
                                          <Box key={item.token} borderWidth="1px" borderRadius="md" p={2}>
                                            <Text fontSize="sm" fontFamily="mono">
                                              {item.token}
                                            </Text>
                                            <Text fontSize="sm">Amount: {item.amount}</Text>
                                            <Text fontSize="sm" fontFamily="mono">
                                              ATA: {item.recipientTokenAccount}
                                            </Text>
                                            <Text fontSize="sm">
                                              ATA created: {item.recipientTokenAccountCreated ? 'yes' : 'no'}
                                            </Text>
                                          </Box>
                                        ))}
                                      </VStack>
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      ) : isWithdrawTabEnabled ? (
                        <Stack spacing={3}>
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={2}>
                              <Text fontWeight="semibold">Write Capabilities</Text>
                              {writeCapabilities ? (
                                <>
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Badge colorScheme={writeCapabilities.writeEnabled ? 'green' : 'yellow'}>
                                      {writeCapabilities.writeEnabled ? 'Write enabled' : 'Write disabled'}
                                    </Badge>
                                    <Badge colorScheme={writeCapabilities.signerAuthorized ? 'green' : 'red'}>
                                      {writeCapabilities.signerAuthorized ? 'Signer authorized' : 'Signer unauthorized'}
                                    </Badge>
                                    <Badge colorScheme="blue">
                                      Max batch items: {writeCapabilities.maxBatchItems}
                                    </Badge>
                                  </HStack>
                                  <Stack spacing={1} fontSize="sm">
                                    <Text fontFamily="mono">Signer: {writeCapabilities.signerAddress}</Text>
                                    {writeCapabilities.ownerAddress && (
                                      <Text fontFamily="mono">Owner: {writeCapabilities.ownerAddress}</Text>
                                    )}
                                    {withdrawalSignerDisplay && (
                                      <Text fontFamily="mono">
                                        Withdrawal signer: {withdrawalSignerDisplay}
                                      </Text>
                                    )}
                                    <Text>
                                      Requires one-of-two: {writeCapabilities.requiresOneOfTwo ? 'yes' : 'no'}
                                    </Text>
                                  </Stack>
                                </>
                              ) : (
                                <Text fontSize="sm" color="gray.500">
                                  Loading write capabilities...
                                </Text>
                              )}
                            </Stack>
                          </Box>

                          {!writeCapabilities?.writeEnabled && (
                            <Alert status="warning">
                              <AlertIcon />
                              Withdraw is unavailable because the deployment signer is not currently
                              authorized on-chain.
                            </Alert>
                          )}

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Audit Reason</Text>
                              <FormControl isRequired>
                                <Textarea
                                  size="sm"
                                  minH="72px"
                                  value={auditReason}
                                  onChange={(event) => setAuditReason(event.target.value)}
                                  placeholder="Why is this treasury withdraw needed?"
                                />
                                <FormHelperText fontSize="xs">
                                  Required for EVM and Tron withdraws. Minimum 3 characters.
                                </FormHelperText>
                              </FormControl>
                            </Stack>
                          </Box>

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Single Withdraw</Text>
                              <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
                                <FormControl>
                                  <FormLabel fontSize="sm">Token</FormLabel>
                                  <DropdownSelect
                                    size="sm"
                                    value={selectedToken}
                                    options={withdrawTokenOptions}
                                    onChange={setSelectedToken}
                                    placeholder="Select token"
                                    isDisabled={withdrawTokenOptions.length === 0}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontSize="sm">Recipient</FormLabel>
                                  {hasWhitelistRecipients ? (
                                    <DropdownSelect
                                      size="sm"
                                      value={recipientAddress}
                                      options={whitelistOptions}
                                      onChange={setRecipientAddress}
                                      placeholder="Select whitelisted recipient"
                                    />
                                  ) : (
                                    <Input
                                      size="sm"
                                      value={recipientAddress}
                                      onChange={(event) => setRecipientAddress(event.target.value)}
                                      placeholder={compatibilityHint}
                                    />
                                  )}
                                  <FormHelperText fontSize="xs">
                                    {hasWhitelistRecipients
                                      ? 'Recipient comes from the current on-chain withdrawal whitelist.'
                                      : 'Preview verifies the manually entered recipient on-chain.'}
                                  </FormHelperText>
                                </FormControl>
                              </Stack>
                              <FormControl>
                                <FormLabel fontSize="sm">Amount</FormLabel>
                                <HStack spacing={3} align="stretch">
                                  <Input
                                    size="sm"
                                    value={withdrawAmount}
                                    onChange={(event) => setWithdrawAmount(event.target.value)}
                                    placeholder="Enter amount"
                                  />
                                  <Box
                                    minW={{ base: '112px', md: '132px' }}
                                    px={3}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bg={amountPreviewBg}
                                    borderColor={amountPreviewBorderColor}
                                    color={amountPreviewColor}
                                  >
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                      {withdrawAmountPreview}
                                    </Text>
                                  </Box>
                                </HStack>
                              </FormControl>
                              <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handlePreviewWithdraw}
                                  isLoading={isPreviewingWithdraw}
                                  isDisabled={!selectedToken || !recipientAddress || !writeCapabilities?.writeEnabled}
                                >
                                  Preview Withdraw
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleExecuteWithdrawGeneric}
                                  isLoading={isExecutingWithdraw}
                                  isDisabled={
                                    !withdrawPreviewResult?.ready
                                    || !writeCapabilities?.writeEnabled
                                    || (isEvmTreasury && (
                                      pendingEvmOperation !== null
                                      || hasNonterminalEvmOperation
                                      || isRefreshingEvmOperation
                                    ))
                                    || (isTronTreasury && (
                                      pendingTronOperation !== null
                                      || hasNonterminalTronOperation
                                      || isRefreshingTronOperation
                                      || tronOperationError !== null
                                    ))
                                  }
                                >
                                  Execute Withdraw
                                </Button>
                              </HStack>
                              {!(whitelist?.addresses?.length) && (
                                <Alert status="warning">
                                  <AlertIcon />
                                  No enumerable whitelisted recipient is currently available for this
                                  treasury. Enter a recipient manually; preview verifies whitelist
                                  status on-chain.
                                </Alert>
                              )}
                              {withdrawPreviewResult && (
                                <Alert
                                  status={withdrawPreviewResult.ready ? 'success' : 'warning'}
                                  flexDirection="column"
                                  alignItems="flex-start"
                                >
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={0.5}>
                                      <Text fontWeight="semibold">Preview</Text>
                                      <Text fontSize="sm">Ready: {withdrawPreviewResult.ready ? 'yes' : 'no'}</Text>
                                      <Text fontSize="sm">Token tracked: {withdrawPreviewResult.tokenTracked ? 'yes' : 'no'}</Text>
                                      <Text>
                                        Recipient whitelisted:{' '}
                                        {withdrawPreviewResult.recipientWhitelisted ? 'yes' : 'no'}
                                      </Text>
                                      <Text fontSize="sm">Can withdraw: {withdrawPreviewResult.canWithdraw ? 'yes' : 'no'}</Text>
                                      <Text>
                                        Signer authorized: {withdrawPreviewResult.signerAuthorized ? 'yes' : 'no'}
                                      </Text>
                                      {withdrawPreviewResult.estimatedGasLimit && (
                                        <Text fontFamily="mono" fontSize="sm">
                                          Estimated gas: {withdrawPreviewResult.estimatedGasLimit}
                                        </Text>
                                      )}
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                            </Stack>
                          </Box>

                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <Stack spacing={3}>
                              <Text fontWeight="semibold">Batch Withdraw</Text>
                              <FormControl>
                                <FormLabel fontSize="sm">Recipient</FormLabel>
                                {hasWhitelistRecipients ? (
                                  <DropdownSelect
                                    size="sm"
                                    value={recipientAddress}
                                    options={whitelistOptions}
                                    onChange={setRecipientAddress}
                                    placeholder="Select whitelisted recipient"
                                  />
                                ) : (
                                  <Input
                                    size="sm"
                                    value={recipientAddress}
                                    onChange={(event) => setRecipientAddress(event.target.value)}
                                    placeholder={compatibilityHint}
                                  />
                                )}
                                <FormHelperText fontSize="xs">
                                  {hasWhitelistRecipients
                                    ? 'Batch withdraw uses the same whitelisted recipient for every token.'
                                    : 'Preview verifies the manually entered recipient on-chain.'}
                                </FormHelperText>
                              </FormControl>
                              {renderBatchItemsEditor()}
                              <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handlePreviewBatchWithdraw}
                                  isLoading={isPreviewingBatchWithdraw}
                                  isDisabled={!recipientAddress || !writeCapabilities?.writeEnabled}
                                >
                                  Preview Batch Withdraw
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleExecuteBatchWithdrawGeneric}
                                  isLoading={isExecutingBatchWithdraw}
                                  isDisabled={
                                    !batchWithdrawPreviewResult?.ready
                                    || !writeCapabilities?.writeEnabled
                                    || (isEvmTreasury && (
                                      pendingEvmOperation !== null
                                      || hasNonterminalEvmOperation
                                      || isRefreshingEvmOperation
                                    ))
                                    || (isTronTreasury && (
                                      pendingTronOperation !== null
                                      || hasNonterminalTronOperation
                                      || isRefreshingTronOperation
                                      || tronOperationError !== null
                                    ))
                                  }
                                >
                                  Execute Batch Withdraw
                                </Button>
                              </HStack>
                              {batchWithdrawPreviewResult && (
                                <Alert
                                  status={batchWithdrawPreviewResult.ready ? 'success' : 'warning'}
                                  flexDirection="column"
                                  alignItems="flex-start"
                                >
                                  <HStack align="start" spacing={2}>
                                    <AlertIcon mt={1} />
                                    <Stack spacing={0.5} width="full">
                                      <Text fontWeight="semibold">Batch Preview</Text>
                                      <Text fontSize="sm">
                                        Signer authorized:{' '}
                                        {batchWithdrawPreviewResult.signerAuthorized ? 'yes' : 'no'}
                                      </Text>
                                      <VStack align="stretch" spacing={1.5}>
                                        {batchWithdrawPreviewResult.items.map((item) => (
                                          <Box key={`${item.token}:${item.amount}`} borderWidth="1px" borderRadius="md" p={2}>
                                            <Text fontFamily="mono" fontSize="sm">
                                              {item.token}
                                            </Text>
                                            <Text fontSize="sm">Amount: {item.amount}</Text>
                                            <Text fontSize="sm">
                                              Token tracked: {item.tokenTracked ? 'yes' : 'no'}
                                            </Text>
                                            <Text fontSize="sm">
                                              Can withdraw: {item.canWithdraw ? 'yes' : 'no'}
                                            </Text>
                                          </Box>
                                        ))}
                                      </VStack>
                                      {batchWithdrawPreviewResult.estimatedGasLimit && (
                                        <Text fontSize="sm" fontFamily="mono">
                                          Estimated gas: {batchWithdrawPreviewResult.estimatedGasLimit}
                                        </Text>
                                      )}
                                    </Stack>
                                  </HStack>
                                </Alert>
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      ) : (
                        <Alert status="info">
                          <AlertIcon />
                          Treasury withdraw actions are unavailable for this contract type.
                        </Alert>
                      )}
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Box>

              </>
              )}
            </Tabs>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={pendingConfirmAction !== null}
        leastDestructiveRef={cancelConfirmRef}
        onClose={() => setPendingConfirmAction(null)}
        isCentered
        size="md"
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {pendingConfirmAction === 'batch_withdraw'
              ? 'Confirm Batch Withdraw'
              : 'Confirm Withdraw'}
          </AlertDialogHeader>
          <AlertDialogBody>
            {pendingConfirmAction === 'batch_withdraw'
              ? (isSolanaTreasury
                  ? 'This will submit an on-chain Solana batch fee withdraw and auto-create the recipient ATA when needed. Continue?'
                  : 'This will submit an on-chain batch fee withdraw using the deployment signer. Continue?')
              : (isSolanaTreasury
                  ? 'This will submit an on-chain Solana fee withdraw and auto-create the recipient ATA when needed. Continue?'
                  : 'This will submit an on-chain fee withdraw using the deployment signer. Continue?')}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelConfirmRef} onClick={() => setPendingConfirmAction(null)}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isLoading={isExecutingWithdraw || isExecutingBatchWithdraw}
              onClick={() => {
                void handleConfirmExecute()
              }}
            >
              {t('common.confirm', { defaultValue: 'Confirm' })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
